/**
 * PublicBrandingTab.jsx — Phase 143 — Registry > Public Branding (Founder-only).
 * Central CMS for the single public /careers page: Vision (Studio Soul),
 * Principles, Gallery, Peer Endorsements — plus the unrelated Client Portal
 * Google Review QR. One doc, one Save — GET/PUT /api/branding/content.
 */
import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Loader2, Plus, Trash2, UploadCloud, ExternalLink, Save } from "lucide-react";

const EMPTY = {
  vision: { eyebrow: "", headline: "", mission: "", vision: "" },
  principles: [],
  gallery: [],
  peer_endorsements: [],
};

export default function PublicBrandingTab() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/branding/content")
      .then(({ data }) => setContent({ ...EMPTY, ...data }))
      .catch((e) => { toast.error(formatApiError(e)); setContent(EMPTY); });
  }, []);

  const set = (path, value) => {
    setContent((c) => {
      const next = structuredClone(c);
      let ref = next;
      const parts = path.split(".");
      for (let i = 0; i < parts.length - 1; i++) ref = ref[parts[i]];
      ref[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { id, updated_at, updated_by, ...payload } = content;
      const { data } = await api.put("/branding/content", payload);
      setContent({ ...EMPTY, ...data });
      toast.success("Public Branding saved — live on /careers");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!content) {
    return <div className="p-10 text-center text-xs text-slate-400 inline-flex items-center gap-2 w-full justify-center"><Loader2 size={12} className="animate-spin" /> Loading…</div>;
  }

  return (
    <div className="space-y-4" data-testid="public-branding-tab">
      <div className="card-flat p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-slate-500">
          Everything here is live on <a href="/careers" target="_blank" rel="noreferrer" className="text-[#F54800] hover:underline inline-flex items-center gap-1">/careers <ExternalLink size={11} /></a> the moment you save.
          {content.updated_at && <span className="ml-2 text-slate-400">Last saved {new Date(content.updated_at).toLocaleString()} by {content.updated_by}</span>}
        </div>
        <Button onClick={save} disabled={saving} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white shrink-0" data-testid="public-branding-save">
          {saving ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Save size={13} className="mr-1.5" />} Save changes
        </Button>
      </div>

      <Accordion type="multiple" defaultValue={["vision"]} className="card-flat divide-y divide-slate-100">
        <Section id="vision" title="The Vision — Mission & Statement">
          <TextField label="Eyebrow" value={content.vision.eyebrow} onChange={(v) => set("vision.eyebrow", v)} testId="branding-vision-eyebrow" />
          <TextAreaField label="Headline" value={content.vision.headline} onChange={(v) => set("vision.headline", v)} rows={2} testId="branding-vision-headline" />
          <TextAreaField label="Mission" value={content.vision.mission} onChange={(v) => set("vision.mission", v)} rows={3} testId="branding-vision-mission" />
          <TextAreaField label="Vision" value={content.vision.vision} onChange={(v) => set("vision.vision", v)} rows={3} testId="branding-vision-vision" />
        </Section>

        <Section id="principles" title="The Philosophy — Design Principles">
          <ObjectListEditor
            label="Principles (shown 01, 02, 03…)" testId="branding-principles"
            items={content.principles} onChange={(v) => set("principles", v)}
            fields={[{ key: "title", label: "Title" }, { key: "description", label: "Description", area: true }]}
            newItem={{ title: "", description: "" }}
          />
        </Section>

        <Section id="gallery" title="A Day in the Life — Image Gallery">
          <ObjectListEditor
            label="Gallery photos" testId="branding-gallery"
            items={content.gallery} onChange={(v) => set("gallery", v)}
            fields={[{ key: "image_url", label: "Image", image: true }, { key: "caption", label: "Caption" }]}
            newItem={{ image_url: "", caption: "" }}
          />
        </Section>

        <Section id="peer_endorsements" title="Peer Endorsements — Quotes">
          <ObjectListEditor
            label="Endorsements" testId="branding-endorsements"
            items={content.peer_endorsements} onChange={(v) => set("peer_endorsements", v)}
            fields={[
              { key: "photo_url", label: "Photo", image: true },
              { key: "quote", label: "Quote", area: true },
              { key: "name", label: "Name" },
              { key: "title_company", label: "Title / Company" },
            ]}
            newItem={{ photo_url: "", quote: "", name: "", title_company: "" }}
          />
        </Section>

        <Section id="google_review_qr" title="Client Portal — Google Review QR Code & Direct Link">
          <GoogleReviewQrSection />
        </Section>
      </Accordion>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="public-branding-save-bottom">
          {saving ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Save size={13} className="mr-1.5" />} Save changes
        </Button>
      </div>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <AccordionItem value={id} className="border-none">
      <AccordionTrigger className="px-4 py-3 text-sm font-semibold text-slate-800 hover:no-underline" data-testid={`branding-section-${id}`}>
        {title}
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-5">{children}</AccordionContent>
    </AccordionItem>
  );
}

function TextField({ label, value, onChange, testId }) {
  return (
    <div className="mb-3">
      <Label className="label-overline">{label}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="rounded-sm mt-1" data-testid={testId} />
    </div>
  );
}

function TextAreaField({ label, value, onChange, rows = 3, testId }) {
  return (
    <div className="mb-3">
      <Label className="label-overline">{label}</Label>
      <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} className="rounded-sm mt-1" data-testid={testId} />
    </div>
  );
}

function ImageField({ value, onChange, testId }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/branding/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };
  return (
    <div className="flex items-center gap-2">
      {value && <img src={value} alt="" className="w-12 h-12 rounded-sm object-cover border border-slate-200 shrink-0" />}
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Image URL — or upload →" className="rounded-sm text-xs" data-testid={testId} />
      <label className="shrink-0 h-9 px-2.5 inline-flex items-center gap-1 border border-slate-200 rounded-sm text-xs cursor-pointer hover:bg-slate-50 text-slate-600" data-testid={`${testId}-upload`}>
        {uploading ? <Loader2 size={12} className="animate-spin" /> : <UploadCloud size={12} />}
        <input type="file" accept="image/*" className="hidden" onChange={upload} />
      </label>
    </div>
  );
}

/** Repeatable list of objects — add / remove / edit named fields. */
function ObjectListEditor({ label, items, onChange, fields, newItem, testId }) {
  const update = (idx, key, val) => {
    const next = items.map((it, i) => (i === idx ? { ...it, [key]: val } : it));
    onChange(next);
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, { ...newItem }]);
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label className="label-overline">{label}</Label>
        <Button size="sm" variant="outline" onClick={add} className="rounded-sm h-7 text-[11px]" data-testid={`${testId}-add`}>
          <Plus size={11} className="mr-1" /> Add
        </Button>
      </div>
      <div className="space-y-3">
        {items.map((item, idx) => (
          <div key={idx} className="border border-slate-200 rounded-sm p-3 relative" data-testid={`${testId}-item-${idx}`}>
            <button onClick={() => remove(idx)} className="absolute top-2 right-2 text-slate-300 hover:text-rose-600" data-testid={`${testId}-remove-${idx}`}>
              <Trash2 size={12} />
            </button>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pr-6">
              {fields.map((f) => (
                <div key={f.key} className={f.area ? "sm:col-span-2" : ""}>
                  <Label className="text-[10px] text-slate-400">{f.label}</Label>
                  {f.image ? (
                    <ImageField value={item[f.key]} onChange={(v) => update(idx, f.key, v)} testId={`${testId}-${idx}-${f.key}`} />
                  ) : f.area ? (
                    <Textarea value={item[f.key] || ""} onChange={(e) => update(idx, f.key, e.target.value)} rows={2} className="rounded-sm text-xs mt-0.5" data-testid={`${testId}-${idx}-${f.key}`} />
                  ) : (
                    <Input value={item[f.key] || ""} onChange={(e) => update(idx, f.key, e.target.value)} className="rounded-sm text-xs mt-0.5" data-testid={`${testId}-${idx}-${f.key}`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-xs text-slate-400 italic py-2">Nothing yet — click Add.</div>}
      </div>
    </div>
  );
}


/** Phase 139 — Google Review QR code upload. Separate doc/endpoint from the
 * rest of Public Branding content (it's a raw image file, not CMS text), so
 * it fetches + saves independently instead of riding along with the big Save. */
function GoogleReviewQrSection() {
  const [qr, setQr] = useState(null); // { url, updated_at, review_link }
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [linkDraft, setLinkDraft] = useState("");
  const [savingLink, setSavingLink] = useState(false);

  const load = () => api.get("/branding/google-review-qr").then(({ data }) => { setQr(data); setLinkDraft(data?.review_link || ""); }).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/branding/google-review-qr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
      toast.success("Google Review QR code updated — live on the Client Portal");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const clear = async () => {
    if (!window.confirm("Remove the Google Review QR code? The 'Leave a Google Review' button will show a placeholder until a new one is uploaded.")) return;
    try { await api.delete("/admin/branding/google-review-qr"); await load(); toast.success("Removed"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const saveLink = async () => {
    setSavingLink(true);
    try {
      await api.put("/admin/branding/google-review-link", { review_link: linkDraft.trim() });
      await load();
      toast.success("Direct review link saved — live everywhere the review CTA appears");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingLink(false); }
  };

  if (loading) return <div className="text-xs text-slate-400 py-2">Loading…</div>;

  return (
    <div>
      <p className="text-xs text-slate-500 mb-3">
        Shown inside the QR modal when a client taps <b>&quot;Leave a Google Review&quot;</b> on their Home page, and inside the monthly Nudge Bar reminder.
      </p>
      <div className="flex items-center gap-4">
        <div className="w-28 h-28 rounded-sm border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0" data-testid="google-review-qr-preview">
          {qr?.url ? <img src={qr.url} alt="Google Review QR" className="w-full h-full object-contain" /> : <span className="text-[10px] text-slate-400 text-center px-2">No QR uploaded yet</span>}
        </div>
        <div className="space-y-2">
          <label className="inline-flex items-center gap-1.5 h-9 px-3 border border-slate-200 rounded-sm text-xs cursor-pointer hover:bg-slate-50 text-slate-700" data-testid="google-review-qr-upload">
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <UploadCloud size={12} />} Upload QR image
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={upload} disabled={uploading} />
          </label>
          {qr?.url && (
            <Button size="sm" variant="outline" onClick={clear} className="rounded-sm text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="google-review-qr-remove">
              <Trash2 size={12} className="mr-1.5" /> Remove
            </Button>
          )}
          {qr?.updated_at && <p className="text-[11px] text-slate-400">Last updated {new Date(qr.updated_at).toLocaleString()}</p>}
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-slate-100">
        <Label className="label-overline">Direct Review Link — used by the "Leave a Review on Desktop" button</Label>
        <div className="flex items-center gap-2 mt-1.5">
          <Input
            value={linkDraft}
            onChange={(e) => setLinkDraft(e.target.value)}
            placeholder="https://g.page/r/…/review"
            className="rounded-sm text-xs"
            data-testid="google-review-link-input"
          />
          <Button size="sm" onClick={saveLink} disabled={savingLink} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white shrink-0" data-testid="google-review-link-save">
            {savingLink ? <Loader2 size={12} className="animate-spin" /> : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}
